import { useState, useEffect, useCallback, useRef } from "react";
import { ReactDiagram } from "gojs-react";
import { tubeColorSet } from "@/config/const";
import * as go from "gojs";
import LinkLabelOnPathDraggingTool from "./LinkLabelOnPathDraggingTool";
import "rc-dialog/assets/index.css";
import { loadOverview } from "./func";
import {message} from "antd";


interface DataSource {
  nodeDataArray: any;
  linkDataArray: any;
}

interface ComponentProps {
  overview?: boolean; //是否显示概览图
  dataSource: DataSource; //流程图数据
  settings?: Settings; //其他设置
  diagrams: any;
  setDiagrams: any;
  globalData?: any;
  onScaleChange?: (scale: number) => void;
  onSelectNode?: (activeTab:string,selectNode:string,selectLink:string) => void;
}

interface Settings {
  readonly?: boolean; //是否只读
}

const Component = (props: ComponentProps) => {
  const {
    dataSource,
    settings = { readonly: false },
    setDiagrams,
    onScaleChange,
    onSelectNode
  } = props;
  const $ = go.GraphObject.make;
  const diagramInstance = useRef<go.Diagram | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const [pageHeight, setPageHeight] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerHeight;
    }
    return 500;
  });
  const [pageWidth, setPageWidth] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth;
    }
    return 500;
  });
  const overviewContainerRef = useRef(null);
  const overviewInstanceRef = useRef<any>(null);
  const [isLinkSelected, setIsLinkSelected] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setPageHeight(window.innerHeight);
      setPageWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);

    // 清理函数，避免内存泄漏
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 创建一个稳定的引用函数，用于在diagram初始化时设置到父组件和本地引用
  const onDiagramInitialized = useCallback(
    (diagram: go.Diagram) => {
      if (diagram) {
        // 更新本地引用，供组件内部使用
        diagramInstance.current = diagram;
        // diagram.isReadOnly = settings.readonly;
        // 直接设置diagram实例到父组件
        setDiagrams(diagram);

        // 通知初始缩放比例
        if (onScaleChange) {
          onScaleChange(Math.round(diagram.scale * 100));
        }

        // 等待初始布局完成
        diagram.addDiagramListener("InitialLayoutCompleted", () => {
          if (overviewContainerRef.current && !overviewInstanceRef.current) {
            const overview = loadOverview(diagram, overviewContainerRef.current);
            overviewInstanceRef.current = overview;
            overview.requestUpdate();
          }
        });

        console.log("Diagram instance successfully initialized and set to parent component");
      }
    },
    [setDiagrams],
  );

  // 添加清理 useEffect
  useEffect(() => {
    return () => {
      if (overviewInstanceRef.current) {
        // 解除观察，释放资源
        overviewInstanceRef.current.observed = null;
      }
    };
  }, []);

  // 监听图表缩放变化（滚轮、缩放按钮等）
  useEffect(() => {
    const diagram = diagramInstance.current;
    if (!diagram || !onScaleChange) return;

    const handler = () => {
      onScaleChange(Math.round(diagram.scale * 100));
    };

    diagram.addDiagramListener("ViewportBoundsChanged", handler);

    return () => {
      diagram.removeDiagramListener("ViewportBoundsChanged", handler);
    };
  }, [onScaleChange]);

  // 更新图表只读状态的函数
  const updateDiagramReadOnlyState = (diagram: any, isReadOnly: boolean) => {
    if (diagram) {
      diagram.isReadOnly = isReadOnly;
    }
  };

  // 当dataSource变化时，确保diagram模型也同步更新
  useEffect(() => 
  {
    if (diagramInstance.current) 
    {
      // 延迟执行，确保 ReactDiagram 组件已用新的 dataSource 完成模型更新
      setTimeout(() => {
        diagramInstance.current?.zoomToFit();
      }, 100);
    }
    // // 这里不需要额外的更新逻辑，因为ReactDiagram组件会自动处理nodeDataArray和linkDataArray的变化
    // // 但我们可以添加一些调试日志来监控数据更新
    // console.log("DataSource updated, diagram data should sync automatically");
  }, [dataSource]);

  const nodeSelectionAdornmentTemplate = new go.Adornment("Auto").add(
    new go.Shape({ fill: null, stroke: "deepskyblue", strokeWidth: 1.5, strokeDashArray: [4, 2] }),
    new go.Placeholder(),
  );

  const nodeRotateAdornmentTemplate = new go.Adornment({
    locationSpot: go.Spot.Center,
    locationObjectName: "ELLIPSE",
  }).add(
    new go.Shape("Ellipse", {
      name: "ELLIPSE",
      cursor: "pointer",
      desiredSize: new go.Size(7, 7),
      fill: "lightblue",
      stroke: "deepskyblue",
    }),
    new go.Shape({
      geometryString: "M-50 3.5 L0 3.5",
      isGeometryPositioned: true,
      stroke: "deepskyblue",
      strokeWidth: 1.5,
      strokeDashArray: [4, 2],
    }),
  );

  const makePort = (name: any, spot: any, output: any, input: any) => {
    // console.log(spot);
    return $(go.Shape, "Circle", {
      fill: null,
      stroke: null, // this is changed to "white" in the showPorts function
      //连接点尺寸
      desiredSize: new go.Size(5, 5),
      alignment: spot,
      alignmentFocus: spot, // align the port on the main Shape
      portId: name, // declare this object to be a "port"
      fromSpot: spot,
      toSpot: spot, // declare where links may connect at this port
      fromLinkable: output,
      toLinkable: input, // declare whether the user may draw links to/from here
      fromLinkableSelfNode: true,
      toLinkableSelfNode: true,
      cursor: "pointer", // show a different cursor to indicate potential link point
    });
  };

  const showPorts = (node: any, show: any) => {
    let diagram = node.diagram;
    if (!diagram || diagram.isReadOnly) return;
    // 移除对diagram.allowLink的检查，因为我们已经在图表初始化时设置了这个属性
    node.ports.each((port: any) => {
      port.stroke = show ? "red" : null;
      port.fill = show ? "red" : null;
    });
  };

  // myContextMenu将在setupTemplates函数内部定义

  // 设置图表模板
  const setupTemplates = (diagram: any) => {
    // 设置模型属性
    diagram.model = $(go.GraphLinksModel, {
      linkFromPortIdProperty: "fromPort",
      linkToPortIdProperty: "toPort",
      linkKeyProperty: "key",
    });

    // 在setupTemplates内部定义右键菜单，确保能正确访问diagram实例
    const myContextMenu = $(go.HTMLInfo, {
      show: () => {
        if (menuRef.current) {
          // 使用viewPoint获取视图坐标，确保菜单位置与图表视图匹配
          const mousePt = diagram.lastInput.viewPoint;
          const selectedPart = diagram.selection.first();
          setIsLinkSelected(selectedPart instanceof go.Link);

          // 设置菜单样式，确保定位正确
          const menu = menuRef.current;
          menu.style.position = "absolute";
          menu.style.left = mousePt.x + "px";
          menu.style.top = mousePt.y + "px";
          menu.style.zIndex = "1000";
          menu.style.display = "block";

          // 确保菜单不超出视口范围
          const rect = menu.getBoundingClientRect();
          if (rect.right > window.innerWidth) {
            menu.style.left = mousePt.x - rect.width + "px";
          }
          if (rect.bottom > window.innerHeight) {
            menu.style.top = mousePt.y - rect.height + "px";
          }
        }
      },
      hide: () => {
        if (menuRef.current) {
          menuRef.current.style.display = "none";
        }
      },
    });

    const tool = new LinkLabelOnPathDraggingTool();
    diagram.toolManager.mouseMoveTools.insertAt(0, tool);

    // 设置group模板
    diagram.groupTemplate = $(
      go.Group,
      "Vertical",
      {
        selectionObjectName: "PANEL", // selection handle goes around shape, not label
        ungroupable: true, // enable Ctrl-Shift-G to ungroup a selected Group
      },
      $(
        go.TextBlock,
        {
          //alignment: go.Spot.Right,
          font: "bold 55px sans-serif",
          isMultiline: false, // don't allow newlines in text
          editable: true, // allow in-place editing by user
          stroke: "rgba(255, 119, 0, 1)",
        },
        new go.Binding("text", "keywords").makeTwoWay(),
        new go.Binding("stroke", "titleColor"),
      ),
      $(
        go.Panel,
        "Auto",
        { name: "PANEL" },
        $(
          go.Shape,
          "Rectangle", // the rectangular shape around the members
          {
            fill: "rgba(128,128,128,0.05)",
            stroke: "rgba(0,0,0,1)",
            strokeWidth: 2,
            strokeDashArray: [50, 30],
            // allow all kinds of links from and to this port
            // fromLinkable: true, fromLinkableSelfNode: true, fromLinkableDuplicates: true,
            // toLinkable: true, toLinkableSelfNode: true, toLinkableDuplicates: true
          },
        ),
        $(go.Placeholder, { margin: 10, background: "transparent" }), // represents where the members are
      ),
      // { // this tooltip Adornment is shared by all groups
      // 	toolTip:
      // 		$("ToolTip",
      // 			$(go.TextBlock, { margin: 4 },
      // 				// bind to tooltip, not to Group.data, to allow access to Group properties
      // 				new go.Binding("text", "", groupInfo).ofObject())
      // 		),
      // 	// the same context menu Adornment is shared by all groups
      // 	contextMenu: partContextMenu
      // }
    );

    diagram.nodeTemplate = $(
      go.Node,
      "Spot", // the Shape will go around the TextBlock
      {
        contextMenu: myContextMenu,
        zOrder: 1,
        mouseEnter: (e, obj) => {
          showPorts(obj.part, true);
        },
        mouseLeave: (e, obj) => {
          showPorts(obj.part, false);
        },
        locationSpot: go.Spot.Center,
      },
      new go.Binding("zOrder", "zOrder"),

      {
        resizable: true,
        resizeObjectName: "SHAPE",
        // selectionObjectName: 'SHAPE',
      },
      {
        selectable: true,
        selectionObjectName: "SHAPE",
        selectionAdornmentTemplate: nodeSelectionAdornmentTemplate,
      },
      {
        rotatable: true,
        rotateObjectName: "SHAPE",
        rotateAdornmentTemplate: nodeRotateAdornmentTemplate,
      },

      // {
      //   selectable: true,
      //   selectionObjectName: 'SHAPE',
      //   // selectionAdornmentTemplate: gojs(
      //   //   go.Adornment,
      //   //   'Auto',
      //   //   gojs(go.Shape, {
      //   //     fill: null,
      //   //     stroke: 'rgba(220,110,5,1)',
      //   //     strokeWidth: 2,
      //   //     // strokeDashArray: [1, 2],
      //   //   }),
      //   //   gojs(go.Placeholder)
      //   // ),
      // },

      $(
        go.Picture,
        {
          name: "SHAPE",
        },

        new go.Binding("source", "icon").makeTwoWay(),
        new go.Binding("angle").makeTwoWay(),
        new go.Binding("desiredSize", "size", go.Size.parse).makeTwoWay(go.Size.stringify),

        // new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
      ),
      makePort("T1", new go.Spot(1 / 20, 0, 0, 0), true, true),
      makePort("T2", new go.Spot(2 / 20, 0, 0, 0), true, true),
      makePort("T3", new go.Spot(3 / 20, 0, 0, 0), true, true),
      makePort("T4", new go.Spot(4 / 20, 0, 0, 0), true, true),
      makePort("T5", new go.Spot(5 / 20, 0, 0, 0), true, true),
      makePort("T6", new go.Spot(6 / 20, 0, 0, 0), true, true),
      makePort("T7", new go.Spot(7 / 20, 0, 0, 0), true, true),
      makePort("T8", new go.Spot(8 / 20, 0, 0, 0), true, true),
      makePort("T9", new go.Spot(9 / 20, 0, 0, 0), true, true),
      makePort("T10", new go.Spot(10 / 20, 0, 0, 0), true, true),
      makePort("T11", new go.Spot(11 / 20, 0, 0, 0), true, true),
      makePort("T12", new go.Spot(12 / 20, 0, 0, 0), true, true),
      makePort("T13", new go.Spot(13 / 20, 0, 0, 0), true, true),
      makePort("T14", new go.Spot(14 / 20, 0, 0, 0), true, true),
      makePort("T15", new go.Spot(15 / 20, 0, 0, 0), true, true),
      makePort("T16", new go.Spot(16 / 20, 0, 0, 0), true, true),
      makePort("T17", new go.Spot(17 / 20, 0, 0, 0), true, true),
      makePort("T18", new go.Spot(18 / 20, 0, 0, 0), true, true),
      makePort("T19", new go.Spot(19 / 20, 0, 0, 0), true, true),
      makePort("T20", new go.Spot(20 / 20, 0, 0, 0), true, true),

      makePort("B1", new go.Spot(1 / 20, 1, 0, 0), true, true),
      makePort("B2", new go.Spot(2 / 20, 1, 0, 0), true, true),
      makePort("B3", new go.Spot(3 / 20, 1, 0, 0), true, true),
      makePort("B4", new go.Spot(4 / 20, 1, 0, 0), true, true),
      makePort("B5", new go.Spot(5 / 20, 1, 0, 0), true, true),
      makePort("B6", new go.Spot(6 / 20, 1, 0, 0), true, true),
      makePort("B7", new go.Spot(7 / 20, 1, 0, 0), true, true),
      makePort("B8", new go.Spot(8 / 20, 1, 0, 0), true, true),
      makePort("B9", new go.Spot(9 / 20, 1, 0, 0), true, true),
      makePort("B10", new go.Spot(10 / 20, 1, 0, 0), true, true),
      makePort("B11", new go.Spot(11 / 20, 1, 0, 0), true, true),
      makePort("B12", new go.Spot(12 / 20, 1, 0, 0), true, true),
      makePort("B13", new go.Spot(13 / 20, 1, 0, 0), true, true),
      makePort("B14", new go.Spot(14 / 20, 1, 0, 0), true, true),
      makePort("B15", new go.Spot(15 / 20, 1, 0, 0), true, true),
      makePort("B16", new go.Spot(16 / 20, 1, 0, 0), true, true),
      makePort("B17", new go.Spot(17 / 20, 1, 0, 0), true, true),
      makePort("B18", new go.Spot(18 / 20, 1, 0, 0), true, true),
      makePort("B19", new go.Spot(19 / 20, 1, 0, 0), true, true),
      makePort("B20", new go.Spot(20 / 20, 1, 0, 0), true, true),

      makePort("L1", new go.Spot(0, 1 / 20, 0, 0), true, true),
      makePort("L2", new go.Spot(0, 2 / 20, 0, 0), true, true),
      makePort("L3", new go.Spot(0, 3 / 20, 0, 0), true, true),
      makePort("L4", new go.Spot(0, 4 / 20, 0, 0), true, true),
      makePort("L5", new go.Spot(0, 5 / 20, 0, 0), true, true),
      makePort("L6", new go.Spot(0, 6 / 20, 0, 0), true, true),
      makePort("L7", new go.Spot(0, 7 / 20, 0, 0), true, true),
      makePort("L8", new go.Spot(0, 8 / 20, 0, 0), true, true),
      makePort("L9", new go.Spot(0, 9 / 20, 0, 0), true, true),
      makePort("L10", new go.Spot(0, 10 / 20, 0, 0), true, true),
      makePort("L11", new go.Spot(0, 11 / 20, 0, 0), true, true),
      makePort("L12", new go.Spot(0, 12 / 20, 0, 0), true, true),
      makePort("L13", new go.Spot(0, 13 / 20, 0, 0), true, true),
      makePort("L14", new go.Spot(0, 14 / 20, 0, 0), true, true),
      makePort("L15", new go.Spot(0, 15 / 20, 0, 0), true, true),
      makePort("L16", new go.Spot(0, 16 / 20, 0, 0), true, true),
      makePort("L17", new go.Spot(0, 17 / 20, 0, 0), true, true),
      makePort("L18", new go.Spot(0, 18 / 20, 0, 0), true, true),
      makePort("L19", new go.Spot(0, 19 / 20, 0, 0), true, true),
      makePort("L20", new go.Spot(0, 20 / 20, 0, 0), true, true),

      makePort("R1", new go.Spot(1, 1 / 20, 0, 0), true, true),
      makePort("R2", new go.Spot(1, 2 / 20, 0, 0), true, true),
      makePort("R3", new go.Spot(1, 3 / 20, 0, 0), true, true),
      makePort("R4", new go.Spot(1, 4 / 20, 0, 0), true, true),
      makePort("R5", new go.Spot(1, 5 / 20, 0, 0), true, true),
      makePort("R6", new go.Spot(1, 6 / 20, 0, 0), true, true),
      makePort("R7", new go.Spot(1, 7 / 20, 0, 0), true, true),
      makePort("R8", new go.Spot(1, 8 / 20, 0, 0), true, true),
      makePort("R9", new go.Spot(1, 9 / 20, 0, 0), true, true),
      makePort("R10", new go.Spot(1, 10 / 20, 0, 0), true, true),
      makePort("R11", new go.Spot(1, 11 / 20, 0, 0), true, true),
      makePort("R12", new go.Spot(1, 12 / 20, 0, 0), true, true),
      makePort("R13", new go.Spot(1, 13 / 20, 0, 0), true, true),
      makePort("R14", new go.Spot(1, 14 / 20, 0, 0), true, true),
      makePort("R15", new go.Spot(1, 15 / 20, 0, 0), true, true),
      makePort("R16", new go.Spot(1, 16 / 20, 0, 0), true, true),
      makePort("R17", new go.Spot(1, 17 / 20, 0, 0), true, true),
      makePort("R18", new go.Spot(1, 18 / 20, 0, 0), true, true),
      makePort("R19", new go.Spot(1, 19 / 20, 0, 0), true, true),
      makePort("R20", new go.Spot(1, 20 / 20, 0, 0), true, true),
      new go.Binding("location", "loc", go.Point.parse).makeTwoWay(go.Point.stringify),

      $(
        go.Panel,
        "Auto",
        {
          alignment: new go.Spot(0.5, 1.2),
          // isActionable: true,
        },
        new go.Binding("alignment", "point", function (x) {
          return new go.Spot(x[0], x[1]);
        }),

        $(
          go.Shape,
          "RoundedRectangle",
          {
            fill: "red",
            stroke: null,
            parameter1: 3,

            // desiredSize: new go.Size(999, 50),
            // cursor: "move",
            // pickable: true,
            // isActionable: true,
          },
          new go.Binding("fill", "textBackground").makeTwoWay(),
        ),

        $(
          go.TextBlock,
          {
            editable: true,
            //元件下方文字尺寸
            font: "12pt Segoe UI,sans-serif",
            stroke: "#000",
            margin: new go.Margin(5, 10, 2, 10),
            // margin: new go.Margin(5, 6, 3, 6),
            // margin: new go.Margin(10,10,10,10),
            // alignment: new go.Spot(0.5, 1.2),
            // minSize: new go.Size(10, 14),
            // alignment: new go.Spot(0.5, 1.05),
            // margin: new go.Margin(10, 0, 10, 0),
          },
          new go.Binding("text", "text").makeTwoWay(),
          new go.Binding("stroke", "textColor").makeTwoWay(),
          // new go.Binding("font", "fontSize", function (size) {
          //   return size + "pt Lato, sans-serif";
          // }).makeTwoWay(),
          new go.Binding("font", "fontSize").makeTwoWay(),
          // new go.Binding("alignment", "point", function (x) {
          //   return new go.Spot(x[0], x[1]);
          // }),
          // new go.Binding("background", "textBackground").makeTwoWay(),
        ),
      ),

      // $(
      //   go.Shape,
      //   'RoundedRectangle',
      //   { strokeWidth: 0, fill: 'white' },
      //   // Shape.fill is bound to Node.data.color
      //   new go.Binding('fill', 'color'),
      // ),
      // $(
      //   go.TextBlock,
      //   { margin: 8 }, // some room around the text
      //   // TextBlock.text is bound to Node.data.key
      //   new go.Binding('text', 'key'),
      // ),
    );
    // );

    // setDiagram(diagram);

    diagram.nodeTemplateMap.add(
      "Comment",
      $(
        go.Node,
        "Auto", // the Shape will go around the TextBlock
        { contextMenu: myContextMenu },
        $(
          // go.Shape,
          // "rectangle",
          // {
          //   strokeWidth: 1,
          //   stroke: "rgba(255,255,255,0.3)",
          //   fill: "rgba(255,255,255,0.1)",
          // },
          go.Picture,
          {
            name: "SHAPE",
          },
          new go.Binding("source", "icon").makeTwoWay(),
          new go.Binding("angle").makeTwoWay(),
          new go.Binding("desiredSize", "size", go.Size.parse).makeTwoWay(go.Size.stringify),

          // Shape.fill is bound to Node.data.color
          // new go.Binding("strokeWidth", "borderWidth").makeTwoWay(),
          // new go.Binding("fill", "bgColor").makeTwoWay(),
          // new go.Binding("stroke", "borderColor").makeTwoWay()
        ),

        $(
          go.TextBlock,
          {
            margin: 4,
            font: "16px sans-serif",
            stroke: "#000",
          }, // Specify a margin to add some room around the text
          // TextBlock.text is bound to Node.data.key
          {
            editable: true,
            alignment: new go.Spot(0.5, 0.5),
          },
          new go.Binding("text").makeTwoWay(),
          // new go.Binding('text', 'text'), //显示控件文字
          // new go.Binding("desiredSize", "size", go.Size.parse).makeTwoWay(go.Size.stringify),
          new go.Binding("stroke", "fontColor").makeTwoWay(),
          new go.Binding("font", "textSize", function (size) {
            return size + "px Lato, sans-serif";
          }).makeTwoWay(),
          new go.Binding("alignment", "point", function (x) {
            return new go.Spot(x[0], x[1]);
          }).makeTwoWay(),
        ),
        new go.Binding("location", "loc", go.Point.parse).makeTwoWay(go.Point.stringify),
        // makePort("T", go.Spot.Top, true, true),
        // makePort("L", go.Spot.Left, true, true),
        // makePort("R", go.Spot.Right, true, true),
        // makePort("B", go.Spot.Bottom, true, true)
      ),
    );

    diagram.linkTemplate = $(
      go.Link,
      // defined below
      { contextMenu: myContextMenu },
      {
        routing: go.Link.AvoidsNodes,
        // contextMenu: myContextMenu,
        corner: 0,
        curve: go.Link.JumpGap,
        relinkableFrom: true,
        relinkableTo: true,
        reshapable: true,
        resegmentable: true,

        // mouseEnter: function (e, link) {
        //   link.findObject('HIGHLIGHT').stroke = 'rgba(30,144,255,0.2)';
        // },
        // mouseLeave: function (e, link) {
        //   link.findObject('HIGHLIGHT').stroke = 'transparent';
        // },
      },
      new go.Binding("points").makeTwoWay(),
      // { contextMenu: myContextMenu },
      $(
        go.Shape, // the highlight shape, normally transparent
        { isPanelMain: true, strokeWidth: 15, stroke: "transparent", name: "HIGHLIGHT" },
      ),
      $(
        go.Shape,
        {
          isPanelMain: true,
          stroke: "rgba(0,0,0,1)",
          strokeWidth: 1,
          // strokeDashArray:[0,0] // 虚线
        },
        new go.Binding("stroke", "lineColor").makeTwoWay(),
        new go.Binding("strokeWidth", "strokeWidth").makeTwoWay(),
        new go.Binding("strokeDashArray", "lineType").makeTwoWay(),
      ),

      $(
        go.Shape, // the highlight shape, normally transparent
        { isPanelMain: true, strokeWidth: 15, stroke: "transparent", name: "HIGHLIGHT" },
      ),
      // $(
      //   go.Shape,
      //   { isPanelMain: true, stroke: "#787878", strokeWidth: 12 },
      //   new go.Binding("stroke", "lineColor1").makeTwoWay(),
      //   new go.Binding("strokeWidth", "strokeWidth1").makeTwoWay()
      // ),
      // $(
      //   go.Shape,
      //   { isPanelMain: true, stroke: "#a3a3a3", strokeWidth: 8 },
      //   new go.Binding("stroke", "lineColor2").makeTwoWay(),
      //   new go.Binding("strokeWidth", "strokeWidth2").makeTwoWay()
      // ),
      // $(
      //   go.Shape,
      //   { isPanelMain: true, stroke: "#dbdbdb", strokeWidth: 4, strokeDashArray: [0, 0] },
      //   new go.Binding("stroke", "lineColor3").makeTwoWay(),
      //   new go.Binding("strokeWidth", "strokeWidth3").makeTwoWay(),
      //   new go.Binding("strokeDashArray", "lineType").makeTwoWay()
      // ),

      // $(go.Shape, { isPanelMain: true, stroke: "#715715", strokeWidth: 6 }),
      // $(go.Shape, { isPanelMain: true, stroke: "#D8B06E", strokeWidth: 2 }),
      // $(go.Shape, { isPanelMain: true, stroke: "#E6C37C" }),
      $(
        go.Shape,
        {
          toArrow: "Triangle",
          stroke: "black",
          fill: "blcak",
          //连接线箭头尺寸
          strokeWidth: 1,
        },
        // new go.Binding("fill", "arrowColor"),
        new go.Binding("strokeWidth", "arrowScale").makeTwoWay(),
        new go.Binding("stroke", "lineColor").makeTwoWay(),
        new go.Binding("fill", "lineColor"),
      ),

      $(
        go.Panel,
        "Auto", // the link label, normally not visible
        { visible: true, name: "LABEL", segmentFraction: 0.5, _isLinkLabel: true },
        new go.Binding("visible", "visible").makeTwoWay(),
        // new go.Binding("segmentIndex", "segmentIndex").makeTwoWay(),
        new go.Binding("segmentFraction", "segmentFraction").makeTwoWay(),
        $(
          go.Shape,
          "Rectangle", // the label shape
          { fill: "#f0fdffff", stroke: "#0273b9ff", strokeWidth: 1 },
          new go.Binding("fill", "shapeBG").makeTwoWay(), //连接线文字背景颜色
          new go.Binding("stroke", "shapeBorder").makeTwoWay(), //连接线文字边框颜色
          new go.Binding("strokeWidth", "shapeBorderWidth").makeTwoWay(), //连接线文字边框宽度
        ),
        $(
          go.TextBlock,
          "Stream", // the label
          {
            textAlign: "center",
            //连接线文字尺寸
            // font: "12pt helvetica, arial, sans-serif",
            stroke: "#333333",
            editable: true,
            margin: new go.Margin(5, 6, 3, 6),
          },
          new go.Binding("text").makeTwoWay(),
          new go.Binding("font", "fontSize").makeTwoWay(),
          new go.Binding("stroke", "textColor").makeTwoWay(),
        ),
      ),
    );

    // 监听链接绘制完成事件
    diagram.addDiagramListener("LinkDrawn", (e) => {
      const link = e.subject; // go.Link 对象
      const linkData = link.data; // 链接对应的数据对象
      if (linkData) {
        const currentText = linkData.text || "";
        diagram.model.setDataProperty(linkData, "linkName", currentText);
      }
    });

    diagram.addDiagramListener("ObjectContextClicked", (e) => 
    {
      e.handled = true;
      e.diagram.currentTool.stopTool();
    });

    // 添加全局点击事件监听，点击菜单外部时隐藏菜单
    document.addEventListener("click", (e: any) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        menuRef.current.style.display = "none";
      }
    });
    diagram.addDiagramListener("AnimationFinished", () => {
      diagram.zoomToFit();
    });
    //双击
    diagram.addDiagramListener("ObjectDoubleClicked", (e) => 
    {
      const part = e.subject.part;
      if (!part) return;
      const node = part.data;
      if (!node) return;
      if(part instanceof go.Node)
      {
        onSelectNode("element",node.nodeName,"");
      }
      else if(part instanceof go.Link)
      {
        onSelectNode("stream","",node.linkName);
      }
    });

    // setDiagrams(diagram)
    return diagram;
  };

  const changeTube = (diagram: go.Diagram, tube: any, type: any) => {
    // diagram.model.setDataProperty(tube, "lineColor1", "purple");
    diagram.model.assignAllDataProperties(tube, {
      lineColor1: tubeColorSet[type][0],
      lineColor2: tubeColorSet[type][1],
      lineColor3: tubeColorSet[type][2],
    });
    // console.log(JSON.parse(diagram.model.toJson()));
    // diagram.model=go.Model.fromJson(JSON.parse(diagram.model.toJson()));
  };

  return (
    <>
      {contextHolder}
      {dataSource && (
        <div style={{ position: "relative" }}>
          <ReactDiagram
            initDiagram={() => {
              // 创建diagram实例

              const diagram: any = $(go.Diagram, {
                allowMove: true,
                "commandHandler.archetypeGroupData": {
                  keywords: "Group",
                  isGroup: true,
                  color: "blue",
                },
                "undoManager.isEnabled": true,
                // 确保滚轮缩放功能正常工作
                "toolManager.mouseWheelBehavior": go.ToolManager.WheelZoom,
                "linkingTool.isUnconnectedLinkValid": true,
                "relinkingTool.isUnconnectedLinkValid": true,
                // 启用自动平移和缩放功能
                // "toolManager.dragSelectingTool.isEnabled": true,
                scrollMode: go.Diagram.InfiniteScroll,
                isReadOnly: settings.readonly,
                // 确保允许链接操作
                allowLink: true,
                allowRelink: true,
                // 添加初始事件监听器
                initialContentAlignment: go.Spot.Center,
                contextMenu: null,
                // 确保图表使用正确的坐标系统和缩放范围
                // scale: 1.0,
                // minScale: 0.1,
                // maxScale: 5.0
              });

              // 初始配置由updateDiagramReadOnlyState函数统一管理

              // 设置模板和模型属性
              setupTemplates(diagram);

              // 调用初始化回调函数，将diagram实例传递给父组件
              onDiagramInitialized(diagram);

              // 设置初始只读状态
              updateDiagramReadOnlyState(diagram, settings.readonly);

              return diagram;
            }}
            divClassName="diagramArea"
            nodeDataArray={dataSource.nodeDataArray || []}
            linkDataArray={dataSource.linkDataArray || []}
            // 添加onModelChange属性以确保模型变化时正确更新
            // onModelChange={(e) => {
            //   // 当模型变化时，可以在这里添加额外的处理逻辑
            //   // console.log("Model changed:", e);
            // }}
            style={{ height: pageHeight-262, border: "0px solid #BBB", margin: 0 }}
            // style={{ height: pageHeight-262,width:pageWidth-1191, border: "0px solid #BBB", margin: 0 }}
          />

        </div>
      )}

    </>
  );
};

export default Component;
